import { useState, useEffect } from 'react';
import { useLang } from '../../../i18n/LanguageContext';

interface AuthModalProps {
  isOpen: boolean;
  defaultTab?: 'signin' | 'signup';
  onClose: () => void;
  onSuccess?: (user: { name: string; email: string }) => void;
}

export default function AuthModal({ isOpen, defaultTab = 'signin', onClose, onSuccess }: AuthModalProps) {
  const { t } = useLang();
  const [tab, setTab] = useState<'signin' | 'signup'>(defaultTab);
  const [signInForm, setSignInForm] = useState({ email: '', password: '' });
  const [signUpForm, setSignUpForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [signInMsg, setSignInMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [signUpMsg, setSignUpMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTab(defaultTab);
  }, [defaultTab]);

  if (!isOpen) return null;

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signInForm.email || !signInForm.password) {
      setSignInMsg({ type: 'error', text: t('Please fill in all fields.', '请填写所有字段。') });
      return;
    }
    setLoading(true);
    setSignInMsg(null);
    await new Promise((r) => setTimeout(r, 800));
    setLoading(false);

    const stored = localStorage.getItem('mystic_users');
    const users: { name: string; email: string; password: string }[] = stored ? JSON.parse(stored) : [];
    const found = users.find((u) => u.email === signInForm.email && u.password === signInForm.password);

    if (!found) {
      setSignInMsg({ type: 'error', text: t('Incorrect email or password.', '邮箱或密码不正确。') });
      return;
    }

    const user = { name: found.name, email: found.email };
    localStorage.setItem('mystic_current_user', JSON.stringify(user));
    setSignInMsg({ type: 'success', text: `${t('Welcome back,', '欢迎回来，')} ${found.name}!` });
    setTimeout(() => {
      onSuccess?.(user);
      onClose();
    }, 700);
  };

  const submitRegistrationToBackend = async (name: string, email: string) => {
    try {
      const now = new Date();
      const registerTime = now.toLocaleString('zh-CN', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
      const body = new URLSearchParams({
        name,
        email,
        register_time: registerTime,
      });
      await fetch('https://readdy.ai/api/form/d752oj2vu4krcbn3be0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // 静默处理，不影响注册流程
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUpForm.name || !signUpForm.email || !signUpForm.password || !signUpForm.confirm) {
      setSignUpMsg({ type: 'error', text: t('Please fill in all fields.', '请填写所有字段。') });
      return;
    }
    if (signUpForm.password !== signUpForm.confirm) {
      setSignUpMsg({ type: 'error', text: t('Passwords do not match.', '两次密码不一致。') });
      return;
    }
    if (signUpForm.password.length < 6) {
      setSignUpMsg({ type: 'error', text: t('Password must be at least 6 characters.', '密码至少需要6个字符。') });
      return;
    }
    setLoading(true);
    setSignUpMsg(null);
    await new Promise((r) => setTimeout(r, 800));
    setLoading(false);

    const stored = localStorage.getItem('mystic_users');
    const users: { name: string; email: string; password: string }[] = stored ? JSON.parse(stored) : [];
    const exists = users.find((u) => u.email === signUpForm.email);
    if (exists) {
      setSignUpMsg({ type: 'error', text: t('This email is already registered.', '该邮箱已被注册。') });
      return;
    }
    users.push({ name: signUpForm.name, email: signUpForm.email, password: signUpForm.password });
    localStorage.setItem('mystic_users', JSON.stringify(users));

    // 同步注册信息到后台表单
    await submitRegistrationToBackend(signUpForm.name, signUpForm.email);

    const user = { name: signUpForm.name, email: signUpForm.email };
    localStorage.setItem('mystic_current_user', JSON.stringify(user));
    setSignUpMsg({ type: 'success', text: `${t('Welcome to Mystic Academy,', '欢迎加入神秘学院，')} ${signUpForm.name}!` });
    setTimeout(() => {
      onSuccess?.(user);
      onClose();
    }, 700);
  };

  const switchTab = (newTab: 'signin' | 'signup') => {
    setTab(newTab);
    setSignInMsg(null);
    setSignUpMsg(null);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>

      <div className="relative z-10 w-full max-w-md mx-4 bg-[#1a0d35] border border-[#d4af37]/30 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative px-8 pt-8 pb-6 text-center border-b border-[#d4af37]/20">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <img
              src="https://static.readdy.ai/image/f4ea41724fdff553620ff2345610d616/46f66eaf14331da7e5a01089d8e059d5.png"
              alt="Logo"
              className="w-10 h-10 object-contain"
            />
            <span className="text-xl font-serif text-[#d4af37]">Mystic Academy</span>
          </div>
          <p className="text-[#c8b6ff] text-sm">{t('Your gateway to mystical wisdom', '开启您的神秘学之旅')}</p>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-[#c8b6ff] hover:text-white transition-colors cursor-pointer rounded-full hover:bg-white/10"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex mx-8 mt-6 bg-[#2d1b4e]/60 rounded-full p-1">
          <button
            onClick={() => switchTab('signin')}
            className={`flex-1 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
              tab === 'signin'
                ? 'bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#1a0d35]'
                : 'text-[#c8b6ff] hover:text-white'
            }`}
          >
            {t('Sign In', '登录')}
          </button>
          <button
            onClick={() => switchTab('signup')}
            className={`flex-1 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
              tab === 'signup'
                ? 'bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#1a0d35]'
                : 'text-[#c8b6ff] hover:text-white'
            }`}
          >
            {t('Sign Up', '注册')}
          </button>
        </div>

        {/* Sign In Form */}
        {tab === 'signin' && (
          <form onSubmit={handleSignIn} className="px-8 py-6 space-y-4">
            <div>
              <label className="block text-[#c8b6ff] text-sm mb-1">{t('Email Address', '邮箱地址')}</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-[#d4af37] text-sm"></i>
                <input
                  type="email"
                  name="email"
                  value={signInForm.email}
                  onChange={(e) => setSignInForm({ ...signInForm, email: e.target.value })}
                  placeholder="your@email.com"
                  className="w-full pl-9 pr-4 py-3 bg-[#2d1b4e]/60 border border-[#d4af37]/30 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d4af37] transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-[#c8b6ff] text-sm mb-1">{t('Password', '密码')}</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-[#d4af37] text-sm"></i>
                <input
                  type="password"
                  name="password"
                  value={signInForm.password}
                  onChange={(e) => setSignInForm({ ...signInForm, password: e.target.value })}
                  placeholder={t('Enter your password', '请输入密码')}
                  className="w-full pl-9 pr-4 py-3 bg-[#2d1b4e]/60 border border-[#d4af37]/30 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d4af37] transition-colors"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button type="button" className="text-[#d4af37] text-xs hover:text-white transition-colors cursor-pointer">
                {t('Forgot password?', '忘记密码？')}
              </button>
            </div>

            {signInMsg && (
              <div className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm ${
                signInMsg.type === 'success'
                  ? 'bg-green-500/20 border border-green-500/30 text-green-300'
                  : 'bg-red-500/20 border border-red-500/30 text-red-300'
              }`}>
                <i className={signInMsg.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
                <span>{signInMsg.text}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#1a0d35] rounded-lg font-semibold hover:shadow-lg hover:shadow-[#d4af37]/30 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center space-x-2 disabled:opacity-60"
            >
              {loading ? (
                <i className="ri-loader-4-line animate-spin text-lg"></i>
              ) : (
                <>
                  <i className="ri-login-circle-line"></i>
                  <span>{t('Sign In', '登录')}</span>
                </>
              )}
            </button>

            <p className="text-center text-[#c8b6ff] text-sm">
              {t('Don\'t have an account?', '还没有账户？')}{' '}
              <button type="button" onClick={() => switchTab('signup')} className="text-[#d4af37] hover:text-white transition-colors cursor-pointer">
                {t('Sign Up', '立即注册')}
              </button>
            </p>
          </form>
        )}

        {/* Sign Up Form */}
        {tab === 'signup' && (
          <form onSubmit={handleSignUp} className="px-8 py-6 space-y-4">
            <div>
              <label className="block text-[#c8b6ff] text-sm mb-1">{t('Full Name', '姓名')}</label>
              <div className="relative">
                <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-[#d4af37] text-sm"></i>
                <input
                  type="text"
                  name="name"
                  value={signUpForm.name}
                  onChange={(e) => setSignUpForm({ ...signUpForm, name: e.target.value })}
                  placeholder={t('Your full name', '请输入您的姓名')}
                  className="w-full pl-9 pr-4 py-3 bg-[#2d1b4e]/60 border border-[#d4af37]/30 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d4af37] transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-[#c8b6ff] text-sm mb-1">{t('Email Address', '邮箱地址')}</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-[#d4af37] text-sm"></i>
                <input
                  type="email"
                  name="email"
                  value={signUpForm.email}
                  onChange={(e) => setSignUpForm({ ...signUpForm, email: e.target.value })}
                  placeholder="your@email.com"
                  className="w-full pl-9 pr-4 py-3 bg-[#2d1b4e]/60 border border-[#d4af37]/30 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d4af37] transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-[#c8b6ff] text-sm mb-1">{t('Password', '密码')}</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-[#d4af37] text-sm"></i>
                <input
                  type="password"
                  name="password"
                  value={signUpForm.password}
                  onChange={(e) => setSignUpForm({ ...signUpForm, password: e.target.value })}
                  placeholder={t('At least 6 characters', '至少6个字符')}
                  className="w-full pl-9 pr-4 py-3 bg-[#2d1b4e]/60 border border-[#d4af37]/30 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d4af37] transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="block text-[#c8b6ff] text-sm mb-1">{t('Confirm Password', '确认密码')}</label>
              <div className="relative">
                <i className="ri-lock-2-line absolute left-3 top-1/2 -translate-y-1/2 text-[#d4af37] text-sm"></i>
                <input
                  type="password"
                  name="confirm"
                  value={signUpForm.confirm}
                  onChange={(e) => setSignUpForm({ ...signUpForm, confirm: e.target.value })}
                  placeholder={t('Repeat your password', '请再次输入密码')}
                  className="w-full pl-9 pr-4 py-3 bg-[#2d1b4e]/60 border border-[#d4af37]/30 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#d4af37] transition-colors"
                />
              </div>
            </div>

            {signUpMsg && (
              <div className={`flex items-center space-x-2 px-4 py-3 rounded-lg text-sm ${
                signUpMsg.type === 'success'
                  ? 'bg-green-500/20 border border-green-500/30 text-green-300'
                  : 'bg-red-500/20 border border-red-500/30 text-red-300'
              }`}>
                <i className={signUpMsg.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
                <span>{signUpMsg.text}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-[#1a0d35] rounded-lg font-semibold hover:shadow-lg hover:shadow-[#d4af37]/30 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center space-x-2 disabled:opacity-60"
            >
              {loading ? (
                <i className="ri-loader-4-line animate-spin text-lg"></i>
              ) : (
                <>
                  <i className="ri-user-add-line"></i>
                  <span>{t('Create Account', '创建账户')}</span>
                </>
              )}
            </button>

            <p className="text-center text-[#c8b6ff] text-sm">
              {t('Already have an account?', '已有账户？')}{' '}
              <button type="button" onClick={() => switchTab('signin')} className="text-[#d4af37] hover:text-white transition-colors cursor-pointer">
                {t('Sign In', '立即登录')}
              </button>
            </p>
          </form>
        )}

        {/* Bottom decoration */}
        <div className="px-8 pb-6 text-center">
          <p className="text-[#c8b6ff]/50 text-xs">
            {t('By continuing, you agree to our', '继续即表示您同意我们的')}{' '}
            <span className="text-[#d4af37]/70 cursor-pointer hover:text-[#d4af37]">{t('Terms of Service', '服务条款')}</span>
            {' '}&amp;{' '}
            <span className="text-[#d4af37]/70 cursor-pointer hover:text-[#d4af37]">{t('Privacy Policy', '隐私政策')}</span>
          </p>
        </div>
      </div>
    </div>
  );
}
